import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Package, TriangleAlert, Users, Target, ArrowRight, Activity } from "lucide-react";
import { api, scopeParams } from "../lib/api";
import { useSession } from "../context/SessionContext";
import KpiCard from "../components/KpiCard";
import HealthPill from "../components/HealthPill";
import ExportButton from "../components/ExportButton";
import BranchMap from "../components/BranchMap";
import ProgressRing from "../components/ProgressRing";
import PillDelta from "../components/PillDelta";
import useCountUp from "../lib/useCountUp";
import { downloadCSV, stamp } from "../lib/csv";
import { exportLeaderboardPDF } from "../lib/pdf";
import { fmtInr } from "../lib/format";
import StoreManagerOverview from "./StoreManagerOverview";

const Overview = () => {
  const { session, bootstrap, focusBranch, focusBranchId, setFocus, clearFocus } = useSession();
  // Store manager sees a purpose-built single-branch scorecard — the chain
  // leaderboard and multi-branch filters are meaningless for a role scoped to
  // exactly one shop.
  if (session?.role === "store_manager") {
    return <StoreManagerOverview />;
  }
  return <ChainOverview
    session={session}
    bootstrap={bootstrap}
    focusBranch={focusBranch}
    focusBranchId={focusBranchId}
    setFocus={setFocus}
    clearFocus={clearFocus}
  />;
};

const ChainOverview = ({ session, bootstrap, focusBranch, focusBranchId, setFocus, clearFocus }) => {
  const isRegional = session?.role === "regional_manager";
  const FILTERS = [
    { id: "all", label: isRegional ? "All in Region" : "All Branches" },
    { id: "attention", label: "Needs Attention" },
  ];

  const [filter, setFilter] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const scope = scopeParams(session, null);
    api
      .get("/overview", { params: { ...scope, filter } })
      .then((r) => mounted && setData(r.data))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [session, filter]);

  const regionFilters =
    session?.role === "owner"
      ? (bootstrap?.regions || []).map((r) => ({ id: r, label: `${r} Chennai` }))
      : [];

  const drill = (branchId) => {
    setFocus(branchId);
    navigate("/app/sales");
  };

  const exportCSV = () => {
    if (!data?.leaderboard) return;
    const headers = [
      { key: "rank", label: "Rank" },
      { key: "branch_name", label: "Branch" },
      { key: "region", label: "Region" },
      { key: "health_score", label: "Health Score" },
      { key: "health_band", label: "Band" },
      { key: "todays_revenue", label: "Today's Revenue (INR)" },
      { key: "wow_delta_pct", label: "Wk/Wk %" },
      { get: (r) => (r.wastage_flag ? "Y" : ""), label: "Wastage Flag" },
      { get: (r) => (r.stockout_flag ? "Y" : ""), label: "Stockout Flag" },
      { get: (r) => (r.staffing_flag ? "Y" : ""), label: "Staffing Flag" },
    ];
    downloadCSV(`angadya-leaderboard-${stamp()}.csv`, headers, data.leaderboard);
  };

  const exportPDF = () => {
    if (!data) return;
    exportLeaderboardPDF({
      scopeLabel: data.scope_label,
      kpis: data.kpis,
      rows: data.leaderboard,
    });
  };

  return (
    <div className="space-y-6" data-testid="overview-page">
      {focusBranch && (
        <div
          className="rounded-2xl flex items-center gap-3 px-4 py-3 ang-fade-up"
          style={{
            background: "linear-gradient(135deg, rgba(0,180,216,0.10) 0%, rgba(124,111,240,0.08) 100%)",
            boxShadow: "inset 0 0 0 1px rgba(0,180,216,0.20)",
          }}
          data-testid="focus-banner"
        >
          <Target size={14} strokeWidth={2.4} style={{ color: "#00B4D8" }} />
          <div className="flex-1 text-sm">
            <span className="text-ang-muted">You&apos;re focused on </span>
            <span className="font-bold">{focusBranch.name}</span>
            <span className="text-ang-muted"> · </span>
            <span className="text-ang-muted">{focusBranch.region} Chennai. Overview always shows the full chain — Sales, Inventory, Procurement and HR are scoped to this branch.</span>
          </div>
          <button
            onClick={clearFocus}
            data-testid="btn-clear-focus-banner"
            className="rounded-full text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 hover:bg-white transition-colors"
            style={{ color: "#14142B", background: "rgba(255,255,255,0.7)" }}
          >
            Clear focus
          </button>
          <button
            onClick={() => navigate("/app/sales")}
            data-testid="btn-open-focused"
            className="hidden sm:inline-flex items-center gap-1 rounded-full text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 text-white transition-transform hover:scale-[1.03]"
            style={{ background: "linear-gradient(135deg, #00B4D8, #7C6FF0)" }}
          >
            Open branch <ArrowRight size={11} strokeWidth={2.4} />
          </button>
        </div>
      )}

      {/* HERO CARD */}
      {!loading && data && (
        <HeroCard
          avgHealth={data.kpis.avg_health_score}
          totalBranches={data.kpis.total_branches}
          needsAttention={data.kpis.branches_needing_attention}
          revenue={data.kpis.todays_revenue}
          revenueDelta={data.kpis.todays_revenue_delta_pct}
          healthTrend={data.trends?.health_14d || []}
        />
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          testId="kpi-todays-revenue"
          label="Today's Revenue"
          countTarget={loading ? 0 : data?.kpis.todays_revenue}
          countFormatter={(n) => fmtInr(n, { compact: true })}
          delta={data?.kpis.todays_revenue_delta_pct}
          hint={`vs. yesterday · ${data?.kpis.total_branches || 0} branches`}
          spark={data?.trends?.revenue_14d}
        />
        <KpiCard
          testId="kpi-avg-health"
          label="Avg. Health Score"
          countTarget={loading ? 0 : data?.kpis.avg_health_score}
          countDecimals={1}
          hint="Composite: sales · wastage · stockouts · staffing"
          spark={data?.trends?.health_14d}
          sparkColor="#00B4D8"
        />
        <KpiCard
          testId="kpi-attention"
          label="Branches · Needs Attention"
          value={loading ? "—" : `${data?.kpis.branches_needing_attention}/${data?.kpis.total_branches}`}
          tone="warn"
          hint="Amber or red band"
          spark={data?.trends?.attention_14d}
          sparkColor="#F5A623"
        />
        <KpiCard
          testId="kpi-wastage"
          label="Inventory at Wastage Risk"
          countTarget={loading ? 0 : data?.kpis.wastage_risk_value}
          countFormatter={(n) => fmtInr(n, { compact: true })}
          tone="danger"
          hint="Perishables ≤ 1 day to expiry"
          spark={data?.trends?.wastage_14d}
          sparkColor="#F04438"
        />
      </div>

      {/* Section header with exports */}
      <div className="flex items-end justify-between pb-1 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-ang-muted">The Leaderboard</div>
          <h2 className="font-extrabold text-2xl mt-1" style={{ letterSpacing: "-0.022em" }}>Branches ranked by health</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="hidden md:inline text-xs text-ang-muted mr-2">Click a row to focus a branch</span>
          <ExportButton
            label="Export CSV"
            testId="btn-export-csv-leaderboard"
            onClick={exportCSV}
            disabled={loading || !data?.leaderboard?.length}
          />
          <ExportButton
            label="Snapshot PDF"
            testId="btn-export-pdf-leaderboard"
            onClick={exportPDF}
            variant="primary"
            disabled={loading || !data?.leaderboard?.length}
          />
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2" data-testid="filter-chips">
        {[...FILTERS, ...regionFilters].map((f) => {
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              data-testid={`filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`rounded-full text-xs uppercase tracking-widest font-bold px-3.5 py-1.5 transition-all ${
                active
                  ? "text-white"
                  : "text-ang bg-white hover:bg-[#EEF0F5]"
              }`}
              style={active ? {
                background: "linear-gradient(135deg, #00B4D8, #7C6FF0)",
                boxShadow: "0 4px 12px rgba(0,180,216,0.28)",
              } : {}}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Location map — Owner & Regional Manager only */}
      {bootstrap && (session.role === "owner" || session.role === "regional_manager") && (
        <BranchMap
          allBranches={
            session.role === "regional_manager"
              ? bootstrap.branches.filter((b) => b.region === session.region)
              : bootstrap.branches
          }
          leaderboard={data?.leaderboard || []}
          focusBranchId={focusBranchId}
          onSelect={(branchId) => {
            setFocus(branchId);
            navigate("/app/sales");
          }}
        />
      )}

      {/* Leaderboard */}
      <LeaderboardCard
        loading={loading}
        rows={data?.leaderboard || []}
        focusBranchId={focusBranchId}
        onRowClick={drill}
      />
    </div>
  );
};

// ================================================================
// HERO CARD

const HeroCard = ({ avgHealth, totalBranches, needsAttention, revenue, revenueDelta, healthTrend }) => {
  // Animate the mega score from 0 → target
  const animatedScore = useCountUp(avgHealth, { duration: 900, decimals: 1 });

  // A composite delta anchor: last-7-day change in health series.
  const healthDelta = useMemo(() => {
    if (!healthTrend?.length || healthTrend.length < 8) return 0;
    const last = healthTrend[healthTrend.length - 1];
    const weekAgo = healthTrend[healthTrend.length - 8];
    return +(last - weekAgo).toFixed(1);
  }, [healthTrend]);

  const band = avgHealth >= 78 ? "green" : avgHealth >= 62 ? "amber" : "red";

  return (
    <section
      data-testid="overview-hero"
      className="ang-hero-bg text-white relative overflow-hidden ang-fade-up"
      style={{
        borderRadius: 26,
        boxShadow: "var(--ang-shadow-hero)",
      }}
    >
      {/* Decorative blobs */}
      <div
        className="ang-blob absolute pointer-events-none"
        style={{
          top: -80, right: -60, width: 280, height: 280,
          background: "radial-gradient(circle, rgba(0,180,216,0.55) 0%, rgba(0,180,216,0) 70%)",
          filter: "blur(28px)",
        }}
      />
      <div
        className="ang-blob absolute pointer-events-none"
        style={{
          bottom: -100, left: -40, width: 320, height: 320,
          background: "radial-gradient(circle, rgba(124,111,240,0.55) 0%, rgba(124,111,240,0) 70%)",
          filter: "blur(32px)",
          animationDelay: "3s",
        }}
      />

      <div className="relative z-10 grid grid-cols-1 md:grid-cols-[1.7fr_1fr] gap-6 items-center px-6 sm:px-8 py-8 sm:py-10">
        {/* LEFT: label + score */}
        <div>
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.75)" }}>
            <Activity size={12} strokeWidth={2.4} />
            Chain Health · Real-time
          </div>
          <div className="mt-4 flex items-baseline gap-4 flex-wrap">
            <span
              className="tabular-nums font-black block leading-none"
              style={{ fontSize: "clamp(52px, 8vw, 76px)", letterSpacing: "-0.04em" }}
              data-testid="hero-avg-health"
            >
              {animatedScore.toFixed(1)}
            </span>
            <div className="flex flex-col gap-1.5">
              <PillDelta value={healthDelta} label="this week" testId="hero-trend-delta" />
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest"
                style={{
                  background: band === "green" ? "rgba(18,183,106,0.22)" :
                              band === "amber" ? "rgba(245,166,35,0.22)" :
                              "rgba(240,68,56,0.22)",
                  color:      band === "green" ? "#88F5B1" :
                              band === "amber" ? "#FFD98A" :
                              "#FF9F94",
                }}
              >
                {band === "green" ? "Healthy chain" : band === "amber" ? "Watch closely" : "At risk"}
              </span>
            </div>
          </div>
          <p className="mt-5 max-w-md text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.72)" }}>
            Composite of sales momentum, wastage discipline, stock availability and staffing coverage — refreshed every hour across {totalBranches} branches.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-6">
            <HeroStat label="Branches" value={totalBranches} />
            <HeroStat label="Need attention" value={needsAttention} accent="#F5A623" />
            <HeroStat label="Today's revenue" value={fmtInr(revenue, { compact: true })} delta={revenueDelta} />
          </div>
        </div>

        {/* RIGHT: progress ring */}
        <div className="flex md:justify-end justify-center">
          <ProgressRing value={avgHealth} size={196} stroke={16} testId="hero-progress-ring">
            <div>
              <div className="text-[9px] font-bold uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.65)" }}>
                Health score
              </div>
              <div
                className="tabular-nums font-black leading-none mt-1"
                style={{ fontSize: 44, letterSpacing: "-0.03em" }}
              >
                {Math.round(animatedScore)}
                <span className="text-lg font-bold opacity-70">%</span>
              </div>
              <div className="text-[10px] font-bold uppercase tracking-widest mt-1 opacity-70">
                of 100
              </div>
            </div>
          </ProgressRing>
        </div>
      </div>
    </section>
  );
};

const HeroStat = ({ label, value, delta, accent }) => (
  <div>
    <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.55)" }}>
      {label}
    </div>
    <div className="mt-1 flex items-baseline gap-2">
      <span
        className="tabular-nums font-extrabold text-xl"
        style={{ letterSpacing: "-0.02em", color: accent || "#FFFFFF" }}
      >
        {value}
      </span>
      {delta != null && <PillDelta value={delta} />}
    </div>
  </div>
);

// ================================================================
// LEADERBOARD

const LeaderboardCard = ({ loading, rows, focusBranchId, onRowClick }) => {
  return (
    <div className="ang-panel overflow-hidden" data-testid="leaderboard-container">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="leaderboard-table">
          <thead>
            <tr className="text-[10px] font-bold uppercase tracking-widest text-ang-muted"
              style={{ borderBottom: "1px solid var(--ang-border)" }}
            >
              <th className="text-left px-5 py-3.5 w-14">Rank</th>
              <th className="text-left px-5 py-3.5">Branch</th>
              <th className="text-left px-5 py-3.5 w-28">Health</th>
              <th className="text-right px-5 py-3.5 w-40">Today&apos;s Revenue</th>
              <th className="text-right px-5 py-3.5 w-28">Wk / Wk</th>
              <th className="text-left px-5 py-3.5 w-52">Flags</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const isFocused = row.branch_id === focusBranchId;
              const tintClass =
                isFocused ? "ang-row-tint-focus"
                : row.health_band === "red" ? "ang-row-tint-red"
                : row.health_band === "amber" ? "ang-row-tint-amber"
                : "";
              return (
                <tr
                  key={row.branch_id}
                  data-testid={`leaderboard-row-${row.branch_id}`}
                  className={`ang-row ang-row-anim cursor-pointer ${tintClass}`}
                  style={{
                    borderTop: i === 0 ? "none" : "1px solid var(--ang-border)",
                    animationDelay: `${Math.min(i, 12) * 40}ms`,
                  }}
                  onClick={() => onRowClick(row.branch_id)}
                >
                  <td className="px-5 py-3.5 tabular-nums font-extrabold text-base" style={{ letterSpacing: "-0.02em" }}>
                    {row.rank}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="text-[15px] font-bold leading-tight flex items-center gap-2" style={{ letterSpacing: "-0.01em" }}>
                      {row.branch_name}
                      {isFocused && <Target size={11} strokeWidth={2.4} style={{ color: "#00B4D8" }} />}
                    </div>
                    <div className="text-[11px] uppercase tracking-widest text-ang-muted mt-0.5 font-semibold">{row.region} Chennai</div>
                  </td>
                  <td className="px-5 py-3.5">
                    <HealthPill score={row.health_score} band={row.health_band} testId={`health-${row.branch_id}`} />
                  </td>
                  <td className="px-5 py-3.5 text-right tabular-nums font-bold">{fmtInr(row.todays_revenue, { compact: true })}</td>
                  <td className="px-5 py-3.5 text-right">
                    <PillDelta value={row.wow_delta_pct} showIcon={false} />
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex gap-1.5 flex-wrap">
                      {row.wastage_flag && (
                        <FlagChip icon={<TriangleAlert size={11} strokeWidth={2.4} />} label="Wastage" color="#F04438" />
                      )}
                      {row.stockout_flag && (
                        <FlagChip icon={<Package size={11} strokeWidth={2.4} />} label="Stock-out" color="#F5A623" />
                      )}
                      {row.staffing_flag && (
                        <FlagChip icon={<Users size={11} strokeWidth={2.4} />} label="Staffing" color="#F5A623" />
                      )}
                      {!row.wastage_flag && !row.stockout_flag && !row.staffing_flag && (
                        <span className="text-[11px] text-ang-faint">—</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan="6" className="px-4 py-10 text-center text-sm text-ang-muted">
                  <AlertCircle size={18} className="inline mr-2" />
                  No branches match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const FlagChip = ({ icon, label, color }) => (
  <span
    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold"
    style={{ color, background: `${color}18` }}
  >
    {icon}
    {label}
  </span>
);

export default Overview;
